/**
 * ReleaseNotesFooter.jsx — iter977
 * ─────────────────────────────────────────────────────────────────────────
 * Compact "what's new" strip at the bottom of the Dashboard.
 *
 * Rules:
 *   • Version-scoped dismissal — user closes v2.4 notes → the strip
 *     re-appears when we ship v2.5.
 *   • Persistence key: `release-notes-dismissed-v{VERSION}` in
 *     localStorage.
 *   • Never blocks content — sits at the bottom of the myday scroll
 *     area, above the mobile bottom-nav, and slides in only after the
 *     first paint so returning users notice it naturally.
 *
 * To ship a new set of notes, bump RELEASE_VERSION and edit BULLETS_BG/EN.
 * Everyone who dismissed the previous version will see the new strip.
 */

import React, { useEffect, useState } from "react";
import { Sparkles, X } from "lucide-react";

// ─── Config — bump this to re-surface the strip for everyone ─────────
const RELEASE_VERSION = "2.4";
const BULLETS_BG = [
  "Основен / Разширен изглед",
  "Бутон „Здраве на гърдата“",
  "2-колонен BHI layout",
];
const BULLETS_EN = [
  "Basic / Expert view toggle",
  "Препратка към „Здраве на гърдата“",
  "2-column BHI layout",
];
// ──────────────────────────────────────────────────────────────────────

const dismissKey = `release-notes-dismissed-v${RELEASE_VERSION}`;

export default function ReleaseNotesFooter({ isDark, bg = true }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(dismissKey) === "1") return;
    } catch { /* localStorage unavailable → still show */ }
    // Delay a bit so it appears after the main content has rendered.
    const t = setTimeout(() => setVisible(true), 600);
    return () => clearTimeout(t);
  }, []);

  if (!visible) return null;

  const dismiss = () => {
    setVisible(false);
    try {
      localStorage.setItem(dismissKey, "1");
    } catch {}
  };

  const bullets = bg ? BULLETS_BG : BULLETS_EN;

  return (
    <div
      data-testid="release-notes-footer"
      className={`mt-6 rounded-full px-3 sm:px-4 py-2 border flex items-center gap-2 text-[11px] sm:text-[12px] ${
        isDark
          ? "bg-slate-900/60 border-slate-700 text-slate-300"
          : "bg-white/80 border-slate-200 text-slate-600 shadow-sm"
      }`}
    >
      <Sparkles
        className={`w-3.5 h-3.5 flex-shrink-0 ${
          isDark ? "text-fuchsia-300" : "text-fuchsia-600"
        }`}
      />
      <span className={`font-bold flex-shrink-0 ${isDark ? "text-white" : "text-slate-900"}`}>
        {bg ? `Ново в v${RELEASE_VERSION}:` : `New in v${RELEASE_VERSION}:`}
      </span>
      <span className="truncate">
        {bullets.join(" • ")}
      </span>
      <button
        type="button"
        onClick={dismiss}
        data-testid="release-notes-dismiss"
        aria-label={bg ? "Затвори" : "Dismiss"}
        className={`ml-auto flex-shrink-0 p-1 rounded-full transition ${
          isDark ? "hover:bg-white/10 text-slate-400" : "hover:bg-slate-100 text-slate-500"
        }`}
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}
